import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, LogOut, RefreshCw, Plus } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Copilot } from "@/components/Copilot";
import { api, money, prettyDate, prettyTs, RES_STYLES, VEHICLE_STYLES } from "@/pages/portal/api";

const LEAD_STYLES = {
  new: "border-aqua/50 bg-aqua/10 text-aqua",
  reviewing: "border-yellow-500/40 bg-yellow-400/10 text-yellow-700",
  contacted: "border-sky-500/40 bg-sky-400/10 text-sky-700",
  approved: "border-emerald-500/40 bg-emerald-400/10 text-emerald-700",
  declined: "border-red-500/40 bg-red-400/10 text-red-600",
};
const LEAD_STATUSES = ["new", "reviewing", "contacted", "approved", "declined"];
const SET_STYLES = { pending: LEAD_STYLES.reviewing, approved: LEAD_STYLES.contacted, paid: LEAD_STYLES.approved };

const TABS = ["Leads", "Partners & Fleet", "Reservations", "Pricing", "Upsells", "Settlements", "Agreements", "Payment Links"];

export default function XePortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Leads");
  return (
    <div className="min-h-screen bg-ink" data-testid="xe-portal">
      <Navbar />
      <div className="border-b border-black/[0.08] bg-white/80 pt-[92px] backdrop-blur">
        <div className="container-x flex flex-wrap items-center justify-between gap-3 py-4">
          <div>
            <p className="eyebrow flex items-center gap-2"><ShieldCheck size={14} /> XE Administrator</p>
            <h1 className="font-display text-xl font-extrabold tracking-tight text-offwhite" data-testid="xe-title">Control Center</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/investors" className="btn-ghost !px-4 !py-2 !text-xs !text-red-700 !border-red-700/30" data-testid="xe-investor-admin-btn">Investor Portal Admin</Link>
            <Link to="/admin/pipeline" className="btn-ghost !px-4 !py-2 !text-xs" data-testid="xe-pipeline-admin-btn">Pipeline</Link>
            <span className="hidden text-xs text-muted sm:block">{user?.email}</span>
            <button onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost !px-4 !py-2 !text-xs" data-testid="xe-signout-btn">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
        <nav className="container-x flex gap-1 overflow-x-auto" aria-label="Admin">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`xe-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${tab === t ? "border-aqua text-offwhite" : "border-transparent text-muted hover:text-offwhite"}`}>
              {t}
            </button>
          ))}
        </nav>
      </div>
      <main className="container-x py-8 pb-24">
        {tab === "Leads" && <LeadsTab />}
        {tab === "Partners & Fleet" && <FleetTab />}
        {tab === "Reservations" && <ReservationsTab />}
        {tab === "Settlements" && <SettlementsTab />}
        {tab === "Agreements" && <ContractsTab />}
        {tab === "Pricing" && <PricingTab />}
        {tab === "Upsells" && <UpsellsTab />}
        {tab === "Payment Links" && <SettingsTab />}
      </main>
      <Copilot audience="admin" />
    </div>
  );
}

function LeadsTab() {
  const [apps, setApps] = useState(null);
  const load = useCallback(() => api.get("/admin/applications").then(({ data }) => setApps(data)).catch(() => toast.error("Could not load leads")), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/applications/${id}`, { status });
      setApps((p) => p.map((a) => (a.application_id === id ? { ...a, status } : a)));
    } catch { toast.error("Status update failed"); }
  };
  if (!apps) return <p className="py-16 text-center text-sm text-muted">Loading…</p>;
  return (
    <div className="space-y-3" data-testid="xe-leads">
      <p className="text-sm text-muted">{apps.length} Fleet Partner applications</p>
      {apps.map((a) => (
        <div key={a.application_id} className="card-line rounded-xl p-5" data-testid={`xe-lead-${a.application_id}`}>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="font-mono text-xs text-aqua">{a.application_id}</span>
            <span className="text-sm font-semibold text-offwhite">{a.legal_business_name}</span>
            <span className="text-xs text-muted">{a.first_name} {a.last_name} · {a.city}, {a.state}</span>
            <span className="text-xs text-muted">{a.email}</span>
            <span className={`ml-auto rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${LEAD_STYLES[a.status || "new"]}`}>{a.status || "new"}</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {LEAD_STATUSES.map((s) => (
              <button key={s} onClick={() => setStatus(a.application_id, s)} data-testid={`xe-lead-status-${a.application_id}-${s}`}
                className={`rounded-full border px-3 py-1 text-[11px] font-semibold capitalize ${(a.status || "new") === s ? LEAD_STYLES[s] : "border-black/10 text-muted hover:border-aqua/40"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FleetTab() {
  const [partners, setPartners] = useState(null);
  const [partnerForm, setPartnerForm] = useState({ name: "", contact_name: "", contact_email: "" });
  const [locForm, setLocForm] = useState({});
  const [vehForm, setVehForm] = useState({});
  const load = useCallback(() => api.get("/admin/partners").then(({ data }) => setPartners(data)).catch(() => toast.error("Could not load partners")), []);
  useEffect(() => { load(); }, [load]);

  const addPartner = async () => {
    try { await api.post("/admin/partners", partnerForm); toast.success("Partner added"); setPartnerForm({ name: "", contact_name: "", contact_email: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addLocation = async (partnerId) => {
    try { await api.post("/admin/locations", { partner_id: partnerId, ...locForm[partnerId] }); toast.success("Location added"); setLocForm((f) => ({ ...f, [partnerId]: {} })); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addVehicle = async (partnerId, locationId) => {
    try { await api.post("/admin/vehicles", { partner_id: partnerId, location_id: locationId, ...vehForm[partnerId] }); toast.success("Vehicle added"); setVehForm((f) => ({ ...f, [partnerId]: {} })); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setVehicleStatus = async (id, status) => {
    try { await api.patch(`/admin/vehicles/${id}/status`, { status }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!partners) return <p className="py-16 text-center text-sm text-muted">Loading…</p>;
  return (
    <div className="space-y-5" data-testid="xe-fleet">
      <div className="card-line rounded-xl p-5">
        <p className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-muted">Add Partner</p>
        <div className="grid gap-3 sm:grid-cols-4">
          <input className="field" placeholder="Partner company name" value={partnerForm.name} onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })} data-testid="partner-name" />
          <input className="field" placeholder="Contact name" value={partnerForm.contact_name} onChange={(e) => setPartnerForm({ ...partnerForm, contact_name: e.target.value })} data-testid="partner-contact" />
          <input className="field" type="email" placeholder="Contact Google email" value={partnerForm.contact_email} onChange={(e) => setPartnerForm({ ...partnerForm, contact_email: e.target.value })} data-testid="partner-email" />
          <button onClick={addPartner} disabled={!partnerForm.name || !partnerForm.contact_email || !partnerForm.contact_name} className="btn-primary !py-2.5 !text-xs disabled:opacity-50" data-testid="partner-add"><Plus size={14} /> Add Partner</button>
        </div>
        <p className="mt-2 text-[11px] text-muted/70">The contact email becomes the partner's portal login — when they sign in with that Google account, they see only their own fleet.</p>
      </div>

      {partners.map((p) => (
        <div key={p.partner_id} className="card-line rounded-xl p-5" data-testid={`xe-partner-${p.partner_id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-display text-lg font-bold text-offwhite">{p.name}</p>
            <span className="text-xs text-muted">{p.contact_name} · {p.contact_email}</span>
          </div>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div>
              <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted">Locations</p>
              {p.locations.map((l) => <p key={l.location_id} className="mb-1 text-sm text-offwhite">{l.name} <span className="text-muted">· {l.address}</span></p>)}
              <div className="mt-2 flex gap-2">
                <input className="field !py-2 text-xs" placeholder="Location name" value={locForm[p.partner_id]?.name || ""} onChange={(e) => setLocForm((f) => ({ ...f, [p.partner_id]: { ...f[p.partner_id], name: e.target.value } }))} data-testid={`loc-name-${p.partner_id}`} />
                <input className="field !py-2 text-xs" placeholder="Address" value={locForm[p.partner_id]?.address || ""} onChange={(e) => setLocForm((f) => ({ ...f, [p.partner_id]: { ...f[p.partner_id], address: e.target.value } }))} data-testid={`loc-address-${p.partner_id}`} />
                <button onClick={() => addLocation(p.partner_id)} className="btn-ghost shrink-0 !px-3 !py-2 !text-xs" data-testid={`loc-add-${p.partner_id}`}><Plus size={13} /></button>
              </div>
            </div>
            <div>
              <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted">Jet Cars</p>
              {p.vehicles.map((v) => (
                <div key={v.vehicle_id} className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-offwhite">{v.name}</span>
                  <span className="text-xs text-muted">{v.color}</span>
                  <select value={v.status} onChange={(e) => setVehicleStatus(v.vehicle_id, e.target.value)} data-testid={`veh-status-${v.vehicle_id}`}
                    className={`ml-auto rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${VEHICLE_STYLES[v.status]}`}>
                    {["available", "reserved", "in_use", "maintenance", "out_of_service"].map((s) => <option key={s} value={s} className="bg-white text-offwhite">{s.replace("_", " ")}</option>)}
                  </select>
                </div>
              ))}
              <div className="mt-2 flex gap-2">
                <input className="field !py-2 text-xs" placeholder="Vehicle name" value={vehForm[p.partner_id]?.name || ""} onChange={(e) => setVehForm((f) => ({ ...f, [p.partner_id]: { ...f[p.partner_id], name: e.target.value } }))} data-testid={`veh-name-${p.partner_id}`} />
                <input className="field !py-2 text-xs" placeholder="Color" value={vehForm[p.partner_id]?.color || ""} onChange={(e) => setVehForm((f) => ({ ...f, [p.partner_id]: { ...f[p.partner_id], color: e.target.value } }))} data-testid={`veh-color-${p.partner_id}`} />
                <button onClick={() => addVehicle(p.partner_id, p.locations[0]?.location_id)} disabled={!p.locations.length} className="btn-ghost shrink-0 !px-3 !py-2 !text-xs disabled:opacity-40" data-testid={`veh-add-${p.partner_id}`}><Plus size={13} /></button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReservationsTab() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/admin/reservations").then(({ data }) => setRows(data)).catch(() => toast.error("Could not load reservations")); }, []);
  if (!rows) return <p className="py-16 text-center text-sm text-muted">Loading…</p>;
  const partners = [...new Set(rows.map((r) => r.partner_name))];
  const visible = filter === "all" ? rows : rows.filter((r) => r.partner_name === filter);
  return (
    <div data-testid="xe-reservations">
      <div className="mb-4 flex flex-wrap gap-2">
        {["all", ...partners].map((p) => (
          <button key={p} onClick={() => setFilter(p)} data-testid={`xe-res-filter-${p.replace(/\s+/g, "-").toLowerCase()}`}
            className={`rounded-full border px-4 py-1.5 text-xs font-semibold ${filter === p ? "border-aqua bg-aqua/15 text-aqua" : "border-black/10 text-muted"}`}>
            {p === "all" ? `All partners (${rows.length})` : p}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {visible.map((r) => (
          <div key={r.reservation_id} className="card-line flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-xl px-5 py-3.5" data-testid={`xe-res-${r.reservation_id}`}>
            <span className="font-mono text-xs text-aqua">{r.reservation_id}</span>
            <span className="text-sm font-semibold text-offwhite">{r.partner_name}</span>
            <span className="text-xs text-muted">{r.vehicle_name}</span>
            <span className="text-xs text-muted">{prettyDate(r.date)} {r.start_time} · {r.duration_hours + r.extensions_hours}h</span>
            <span className="text-xs text-muted">{r.customer.name}</span>
            <span className="text-xs font-semibold text-offwhite">{money(r.price)}</span>
            <span className={`text-[11px] font-semibold ${r.payment_status === "paid" ? "text-emerald-700" : "text-orange-600"}`}>{r.payment_status}</span>
            <span className={`ml-auto rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${RES_STYLES[r.status]}`}>{r.status.replace("_", " ")}</span>
          </div>
        ))}
        {visible.length === 0 && <p className="card-line rounded-xl py-12 text-center text-sm text-muted">No reservations in this view.</p>}
      </div>
    </div>
  );
}

function SettlementsTab() {
  const [rows, setRows] = useState(null);
  const [adj, setAdj] = useState({});
  const load = useCallback(() => api.get("/admin/settlements").then(({ data }) => setRows(data)).catch(() => toast.error("Could not load settlements")), []);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  return (
    <div data-testid="xe-settlements">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-xl text-sm text-muted">Generate last week's settlement per partner from completed rentals. Review adjustments, approve, then mark Wednesday payouts as paid.</p>
        <button onClick={() => act(() => api.post("/admin/settlements/generate"), "Settlements generated")} className="btn-primary !py-2.5 !text-xs" data-testid="generate-settlements-btn">
          <RefreshCw size={13} /> Generate Last Week
        </button>
      </div>
      {rows === null ? <p className="py-16 text-center text-sm text-muted">Loading…</p> : rows.length === 0 ? (
        <p className="card-line rounded-xl py-12 text-center text-sm text-muted" data-testid="settlements-empty">No settlements yet — generate last week's above.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((s) => (
            <div key={s.settlement_id} className="card-line rounded-xl p-5" data-testid={`xe-settlement-${s.settlement_id}`}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <span className="font-mono text-xs text-aqua">{s.settlement_id}</span>
                <span className="text-sm font-semibold text-offwhite">{s.partner_name}</span>
                <span className="text-xs text-muted">{prettyDate(s.week_start)} – {prettyDate(s.week_end)}</span>
                <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${SET_STYLES[s.status]}`}>{s.status}</span>
                <span className="ml-auto text-xs text-muted">Payout Wed {prettyDate(s.payout_date)}{s.paid_at ? ` · paid ${prettyTs(s.paid_at)}` : ""}</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
                <div><p className="font-mono text-[10px] uppercase tracking-widest text-muted">Hours</p><p className="font-display text-lg font-bold text-offwhite">{s.eligible_hours}</p></div>
                <div><p className="font-mono text-[10px] uppercase tracking-widest text-muted">Gross</p><p className="font-display text-lg font-bold text-offwhite">{money(s.gross_revenue)}</p></div>
                <div><p className="font-mono text-[10px] uppercase tracking-widest text-muted">Split</p><p className="font-display text-lg font-bold text-offwhite">{s.partner_pct}/{100 - s.partner_pct}</p></div>
                <div><p className="font-mono text-[10px] uppercase tracking-widest text-muted">Partner</p><p className="font-display text-lg font-bold text-aqua">{money(s.partner_amount)}</p></div>
                <div><p className="font-mono text-[10px] uppercase tracking-widest text-muted">XE</p><p className="font-display text-lg font-bold text-offwhite">{money(s.xe_amount)}</p></div>
              </div>
              {s.adjustments?.length > 0 && (
                <div className="mt-3 border-t border-black/[0.08] pt-2">
                  {s.adjustments.map((a, i) => <p key={i} className="text-xs text-muted">{money(a.amount)} — {a.reason} <span className="text-muted/60">({a.by})</span></p>)}
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-black/[0.08] pt-4">
                <input className="field w-28 !py-2 text-xs" type="number" placeholder="± amount" value={adj[s.settlement_id]?.amount || ""} onChange={(e) => setAdj((f) => ({ ...f, [s.settlement_id]: { ...f[s.settlement_id], amount: e.target.value } }))} data-testid={`adj-amount-${s.settlement_id}`} />
                <input className="field flex-1 !py-2 text-xs" placeholder="Adjustment reason" value={adj[s.settlement_id]?.reason || ""} onChange={(e) => setAdj((f) => ({ ...f, [s.settlement_id]: { ...f[s.settlement_id], reason: e.target.value } }))} data-testid={`adj-reason-${s.settlement_id}`} />
                <button disabled={s.status === "paid" || !adj[s.settlement_id]?.amount || !adj[s.settlement_id]?.reason}
                  onClick={() => act(() => api.post(`/admin/settlements/${s.settlement_id}/adjustments`, { amount: parseFloat(adj[s.settlement_id].amount), reason: adj[s.settlement_id].reason }), "Adjustment added")}
                  className="btn-ghost !px-4 !py-2 !text-xs disabled:opacity-40" data-testid={`adj-add-${s.settlement_id}`}>Add Adjustment</button>
                {s.status === "pending" && (
                  <button onClick={() => act(() => api.post(`/admin/settlements/${s.settlement_id}/approve`), "Settlement approved")} className="btn-primary !px-4 !py-2 !text-xs" data-testid={`approve-${s.settlement_id}`}>Approve</button>
                )}
                {s.status === "approved" && (
                  <button onClick={() => act(() => api.post(`/admin/settlements/${s.settlement_id}/pay`), "Marked as paid")} className="btn-primary !px-4 !py-2 !text-xs" data-testid={`pay-${s.settlement_id}`}>Mark Paid</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const [form, setForm] = useState(null);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setForm({ links: { "3": "", "4": "", "5": "", extension: "", ...data.links }, hourly_rate: data.hourly_rate })).catch(() => toast.error("Could not load settings")); }, []);
  if (!form) return <p className="py-16 text-center text-sm text-muted">Loading…</p>;
  const save = async () => {
    try { await api.put("/admin/settings", form); toast.success("Payment settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const FIELDS = [["3", "3-hour rental Payment Link"], ["4", "4-hour rental Payment Link"], ["5", "5-hour rental Payment Link"], ["extension", "Hourly extension Payment Link"]];
  return (
    <div className="max-w-2xl" data-testid="xe-settings">
      <p className="mb-5 text-sm leading-relaxed text-muted">
        Paste the XE-controlled Stripe Payment Link URLs here. Partners see the matching link and QR code for each
        reservation duration. Automatic payment confirmation via Stripe webhook will be enabled when the Stripe account
        is connected — the endpoint is already prepared at <span className="font-mono text-xs">/api/webhook/stripe</span>.
      </p>
      <div className="card-line space-y-4 rounded-xl p-6">
        {FIELDS.map(([key, label]) => (
          <div key={key}>
            <label className="field-label" htmlFor={`link-${key}`}>{label}</label>
            <input id={`link-${key}`} className="field font-mono text-xs" placeholder="https://buy.stripe.com/…" value={form.links[key] || ""}
              onChange={(e) => setForm((f) => ({ ...f, links: { ...f.links, [key]: e.target.value } }))} data-testid={`settings-link-${key}`} />
          </div>
        ))}
        <div>
          <label className="field-label" htmlFor="hourly-rate">Hourly rental rate (USD)</label>
          <input id="hourly-rate" type="number" className="field w-40" value={form.hourly_rate}
            onChange={(e) => setForm((f) => ({ ...f, hourly_rate: parseFloat(e.target.value) || 0 }))} data-testid="settings-hourly-rate" />
        </div>
        <button onClick={save} className="btn-primary !py-2.5 !text-xs" data-testid="settings-save">Save Settings</button>
      </div>
    </div>
  );
}

const AGREEMENT_TEMPLATE = `FLEET PARTNERSHIP AGREEMENT

Insurance carrier: XINSURANCE

This fleet-partnership agreement between Xtreme Exotics Watersports LLC ("XE") and the Fleet Partner governs:

1. POSSESSION — custody and possession of XE-owned jet cars assigned to the Partner's location.
2. OPERATIONS — daily rental operations, staffing, safety standards, and brand standards.
3. INSURANCE — primary commercial watercraft/rental liability and physical-damage coverage for owner-titled Jet Cars placed with independent rental operators, provided through XINSURANCE. The Fleet Partner is added as an additional insured on the policy and is required to contribute 45% toward the deductible, not to exceed $2,500. Partner insurance obligations and verification requirements.
4. MAINTENANCE — maintenance standards, technical support, and fleet requirements coordinated by XE.
5. INCIDENT REPORTING — required reporting of damage, incidents, and claims.
6. REVENUE SHARING — weekly settlement based on Eligible Rental Hours (0–15 hours: 40% Partner / 60% XE; 16+ hours: 60% Partner / 40% XE applied retroactively to all eligible hours that week).

[Replace or extend with your attorney-approved agreement text before publishing.]`;

function ContractsTab() {
  const [contracts, setContracts] = useState(null);
  const [status, setStatus] = useState([]);
  const [form, setForm] = useState({ title: "", version: "1.0", body: AGREEMENT_TEMPLATE, required: true });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/contracts").then(({ data }) => setContracts(data)).catch(() => toast.error("Could not load agreements"));
    api.get("/admin/contract-status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/contracts", form);
      toast.success("Agreement published — partners must sign before deployment");
      setForm({ title: "", version: "1.0", body: AGREEMENT_TEMPLATE, required: true });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (id) => {
    try { await api.post(`/admin/contracts/${id}/deactivate`); toast.success("Agreement deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid items-start gap-6 lg:grid-cols-2" data-testid="xe-contracts">
      <div className="card-line rounded-xl p-6">
        <p className="mb-1 font-display text-sm font-bold uppercase tracking-widest text-muted">Publish Agreement</p>
        <p className="mb-4 text-[11px] leading-relaxed text-muted/70">
          Partners must sign every active required agreement before they can create reservations. Publishing a new
          version requires partners to re-sign. Have your attorney review agreement text before publishing.
        </p>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <input className="field col-span-2" placeholder="Agreement title (e.g. Fleet Partner Agreement)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="contract-title" />
            <input className="field" placeholder="Version" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} data-testid="contract-version" />
          </div>
          <textarea rows={10} className="field resize-y font-mono text-xs" placeholder="Full agreement text…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="contract-body" />
          <label className="flex cursor-pointer items-center gap-2.5 text-sm text-muted">
            <input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} className="h-4 w-4 accent-[#00A8A2]" data-testid="contract-required" />
            Required before deployment
          </label>
          <button onClick={create} disabled={busy || !form.title.trim() || !form.body.trim() || !form.version.trim()} className="btn-primary !py-2.5 !text-xs disabled:opacity-40" data-testid="contract-publish">
            <Plus size={14} /> Publish Agreement
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="card-line rounded-xl p-5">
          <p className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-muted">Partner Signature Status</p>
          {status.length === 0 ? <p className="text-xs text-muted">No partners yet.</p> : status.map((s) => (
            <div key={s.partner_id} className="mb-2 flex flex-wrap items-center gap-2" data-testid={`contract-status-${s.partner_id}`}>
              <span className="text-sm font-semibold text-offwhite">{s.partner_name}</span>
              <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${s.cleared_for_deployment ? "border-emerald-500/40 bg-emerald-400/10 text-emerald-700" : "border-orange-500/40 bg-orange-400/10 text-orange-700"}`}>
                {s.cleared_for_deployment ? "Cleared for deployment" : `${s.unsigned.length} unsigned`}
              </span>
              {!s.cleared_for_deployment && <span className="text-[11px] text-muted">{s.unsigned.map((u) => u.title).join(", ")}</span>}
            </div>
          ))}
        </div>

        {contracts === null ? <p className="py-8 text-center text-sm text-muted">Loading…</p> : contracts.length === 0 ? (
          <p className="card-line rounded-xl py-10 text-center text-sm text-muted" data-testid="contracts-empty">No agreements published yet.</p>
        ) : contracts.map((c) => (
          <div key={c.contract_id} className="card-line rounded-xl p-5" data-testid={`xe-contract-${c.contract_id}`}>
            <div className="flex flex-wrap items-center gap-2.5">
              <p className="font-display text-base font-bold text-offwhite">{c.title}</p>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">v{c.version}</span>
              {c.required && <span className="rounded-full border border-black/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-muted">Required</span>}
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${c.active ? "border-aqua/50 bg-aqua/10 text-aqua" : "border-black/15 text-muted"}`}>{c.active ? "Active" : "Inactive"}</span>
              {c.active && (
                <button onClick={() => deactivate(c.contract_id)} className="ml-auto text-[11px] font-semibold text-red-600 hover:underline" data-testid={`contract-deactivate-${c.contract_id}`}>Deactivate</button>
              )}
            </div>
            <p className="mt-1 text-xs text-muted">{c.signatures?.length || 0} signature{c.signatures?.length === 1 ? "" : "s"} · published {prettyTs(c.created_at)}</p>
            {c.signatures?.length > 0 && (
              <div className="mt-2 border-t border-black/[0.08] pt-2">
                {c.signatures.map((s, i) => (
                  <p key={i} className="text-xs text-muted">
                    <span className="text-offwhite">{s.partner_name}</span> — signed by {s.typed_name} ({s.signed_by}) · {prettyTs(s.signed_at)} · v{s.contract_version}
                  </p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PricingTab() {
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState({ location_id: "", min_rate: "", standard_rate: "", peak_rate: "", offseason_rate: "", peak_from: "", peak_to: "", off_from: "", off_to: "", blackouts: "", effective_from: "", effective_to: "" });
  const load = useCallback(() => api.get("/admin/pricing").then(({ data }) => setData(data)).catch(() => toast.error("Could not load pricing")), []);
  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    try {
      await api.post("/admin/pricing/plans", {
        location_id: plan.location_id,
        min_rate: parseFloat(plan.min_rate), standard_rate: parseFloat(plan.standard_rate),
        peak_rate: parseFloat(plan.peak_rate), offseason_rate: parseFloat(plan.offseason_rate),
        peak_ranges: plan.peak_from && plan.peak_to ? [{ start: plan.peak_from, end: plan.peak_to }] : [],
        offseason_ranges: plan.off_from && plan.off_to ? [{ start: plan.off_from, end: plan.off_to }] : [],
        blackout_dates: plan.blackouts ? plan.blackouts.split(",").map((d) => d.trim()).filter(Boolean) : [],
        effective_from: plan.effective_from || null, effective_to: plan.effective_to || null,
      });
      toast.success("Pricing plan published");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
  };

  const review = async (id, action) => {
    try { await api.post(`/admin/pricing/requests/${id}/${action}`, { note: "" }); toast.success(`Request ${action}d`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!data) return <p className="py-16 text-center text-sm text-muted">Loading…</p>;
  const pending = data.requests.filter((r) => r.status === "pending");
  const planValid = plan.location_id && plan.min_rate && plan.standard_rate && plan.peak_rate && plan.offseason_rate;

  return (
    <div className="grid items-start gap-6 lg:grid-cols-2" data-testid="xe-pricing">
      <div className="space-y-5">
        <div className="card-line rounded-xl p-6">
          <p className="mb-1 font-display text-sm font-bold uppercase tracking-widest text-muted">Publish Pricing Plan</p>
          <p className="mb-4 text-[11px] text-muted/70">XE has final authority over all rental prices. Publishing supersedes the location's current plan and is fully audited.</p>
          <div className="space-y-3">
            <select className="field" value={plan.location_id} onChange={(e) => setPlan({ ...plan, location_id: e.target.value })} data-testid="plan-location">
              <option value="">Location…</option>
              {data.locations.map((l) => <option key={l.location_id} value={l.location_id} className="bg-white">{l.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              {[["min_rate", "Approved minimum $/hr"], ["standard_rate", "Standard $/hr"], ["peak_rate", "Peak $/hr"], ["offseason_rate", "Off-season $/hr"]].map(([k, label]) => (
                <input key={k} type="number" min="1" className="field" placeholder={label} value={plan[k]} onChange={(e) => setPlan({ ...plan, [k]: e.target.value })} data-testid={`plan-${k}`} />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="date" className="field" value={plan.peak_from} onChange={(e) => setPlan({ ...plan, peak_from: e.target.value })} data-testid="plan-peak-from" title="Peak from" />
              <input type="date" className="field" value={plan.peak_to} onChange={(e) => setPlan({ ...plan, peak_to: e.target.value })} data-testid="plan-peak-to" title="Peak to" />
              <input type="date" className="field" value={plan.off_from} onChange={(e) => setPlan({ ...plan, off_from: e.target.value })} data-testid="plan-off-from" title="Off-season from" />
              <input type="date" className="field" value={plan.off_to} onChange={(e) => setPlan({ ...plan, off_to: e.target.value })} data-testid="plan-off-to" title="Off-season to" />
            </div>
            <input className="field" placeholder="Blackout dates (YYYY-MM-DD, comma separated)" value={plan.blackouts} onChange={(e) => setPlan({ ...plan, blackouts: e.target.value })} data-testid="plan-blackouts" />
            <div className="grid grid-cols-2 gap-3">
              <input type="date" className="field" value={plan.effective_from} onChange={(e) => setPlan({ ...plan, effective_from: e.target.value })} data-testid="plan-eff-from" title="Effective from" />
              <input type="date" className="field" value={plan.effective_to} onChange={(e) => setPlan({ ...plan, effective_to: e.target.value })} data-testid="plan-eff-to" title="Effective to" />
            </div>
            <p className="text-[11px] text-muted/70">Minimum rental duration is fixed at 3 hours program-wide.</p>
            <button onClick={publish} disabled={!planValid} className="btn-primary !py-2.5 !text-xs disabled:opacity-40" data-testid="plan-publish">Publish Plan</button>
          </div>
        </div>

        <div className="card-line rounded-xl p-5">
          <p className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-muted">Active Plans & Audit History</p>
          {data.plans.length === 0 && <p className="text-xs text-muted">No plans yet.</p>}
          {data.plans.map((p) => (
            <div key={p.plan_id} className={`mb-3 rounded-lg border p-4 ${p.status === "active" ? "border-aqua/40 bg-aqua/[0.05]" : "border-black/[0.08]"}`} data-testid={`plan-${p.plan_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-bold text-offwhite">{p.location_name}</span>
                <span className="text-xs text-muted">{p.partner_name}</span>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${p.status === "active" ? "border-emerald-500/40 bg-emerald-400/10 text-emerald-700" : "border-black/15 text-muted"}`}>{p.status}</span>
              </div>
              <p className="mt-1.5 text-xs text-muted">min {money(p.min_rate)} · std {money(p.standard_rate)} · peak {money(p.peak_rate)} · off {money(p.offseason_rate)} /hr</p>
              {(p.audit || []).map((a, i) => (
                <p key={i} className="mt-1 text-[11px] text-muted/70">
                  {a.event} — {a.by} · {prettyTs(a.at)}
                  {a.previous_rates && ` (was std ${money(a.previous_rates.standard_rate)})`}
                </p>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="card-line rounded-xl p-5">
        <p className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-muted">Partner Price-Change Requests {pending.length > 0 && <span className="ml-1 rounded-full bg-orange-400/15 px-2 py-0.5 text-orange-700">{pending.length} pending</span>}</p>
        {data.requests.length === 0 && <p className="text-xs text-muted" data-testid="requests-empty">No requests yet.</p>}
        {data.requests.map((r) => (
          <div key={r.request_id} className="mb-3 rounded-lg border border-black/[0.08] p-4" data-testid={`xe-request-${r.request_id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-aqua">{r.request_id}</span>
              <span className="text-sm font-semibold text-offwhite">{r.partner_name}</span>
              <span className="text-xs text-muted">{r.location_name}</span>
              <span className="ml-auto text-[11px] text-muted">{prettyTs(r.created_at)}</span>
            </div>
            <p className="mt-1.5 text-xs text-muted">
              {r.previous_rates ? <>std {money(r.previous_rates.standard_rate)} → </> : "new plan: "}<strong className="text-offwhite">{money(r.proposed_rates.standard_rate)}/hr</strong>
              {" · "}min {money(r.proposed_rates.min_rate)} · peak {money(r.proposed_rates.peak_rate)} · off {money(r.proposed_rates.offseason_rate)}
              {r.effective_from && <> · effective {prettyDate(r.effective_from)}</>}
            </p>
            <p className="mt-1 text-xs italic text-muted/80">"{r.reason}"</p>
            {r.status === "pending" ? (
              <div className="mt-3 flex gap-2">
                <button onClick={() => review(r.request_id, "approve")} className="btn-primary !px-4 !py-2 !text-xs" data-testid={`approve-request-${r.request_id}`}>Approve & Publish</button>
                <button onClick={() => review(r.request_id, "reject")} className="btn-ghost !px-4 !py-2 !text-xs !text-red-600 !border-red-500/40" data-testid={`reject-request-${r.request_id}`}>Reject</button>
              </div>
            ) : (
              <p className="mt-2 text-[11px] text-muted/70">{r.status} by {r.reviewed_by} · {prettyTs(r.reviewed_at)}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function UpsellsTab() {
  const [data, setData] = useState(null);
  const [reports, setReports] = useState(null);
  const [form, setForm] = useState({ name: "", category: "photography", description: "", price: "", provider_type: "xe", locations: [], tax_pct: 0 });
  const [refund, setRefund] = useState({});
  const load = useCallback(() => {
    api.get("/admin/upsells").then(({ data }) => setData(data)).catch(() => toast.error("Could not load upsells"));
    api.get("/admin/upsell-reports").then(({ data }) => setReports(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const create = () => act(() => api.post("/admin/upsells", { ...form, price: parseFloat(form.price), tax_pct: parseFloat(form.tax_pct) || 0 }), "Upsell published");
  const c2 = (n) => `$${((n || 0) / 100).toFixed(2)}`;

  if (!data) return <p className="py-16 text-center text-sm text-muted">Loading…</p>;
  const proposals = data.upsells.filter((u) => u.status === "pending_approval");

  return (
    <div className="space-y-6" data-testid="xe-upsells">
      <div className="grid gap-3 sm:grid-cols-4" data-testid="upsell-reports">
        {[["XE upsell earnings", c2(reports?.xe_earnings_cents?.XE)], ["Partner upsell earnings", c2(Object.values(reports?.partner_earnings_cents || {}).reduce((a, b) => a + b, 0))], ["Refunds/chargebacks", c2(reports?.refunds_chargebacks_cents)], ["Outstanding payouts", c2(Object.values(reports?.outstanding_partner_payouts_cents || {}).reduce((a, b) => a + b, 0))]].map(([label, val]) => (
          <div key={label} className="card-line rounded-xl p-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted">{label}</p>
            <p className="mt-1 font-display text-xl font-extrabold text-offwhite">{val}</p>
          </div>
        ))}
      </div>
      {reports && (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="card-line rounded-xl p-4"><p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted">Sales by category</p>{Object.entries(reports.sales_by_category_cents).map(([k, v]) => <p key={k} className="text-xs text-muted">{k.replace("_", " ")}: <strong className="text-offwhite">{c2(v)}</strong></p>)}{!Object.keys(reports.sales_by_category_cents).length && <p className="text-xs text-muted">—</p>}</div>
          <div className="card-line rounded-xl p-4"><p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted">Sales by location</p>{Object.entries(reports.sales_by_location_cents).map(([k, v]) => <p key={k} className="text-xs text-muted">{k}: <strong className="text-offwhite">{c2(v)}</strong></p>)}{!Object.keys(reports.sales_by_location_cents).length && <p className="text-xs text-muted">—</p>}</div>
          <div className="card-line rounded-xl p-4"><p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted">Unfulfilled orders ({reports.unfulfilled_orders.length})</p>{reports.unfulfilled_orders.slice(0, 5).map((u, i) => <p key={i} className="text-xs text-muted">{u.order_id} · {u.upsell} · {u.status}</p>)}{!reports.unfulfilled_orders.length && <p className="text-xs text-muted">All clear</p>}</div>
        </div>
      )}

      <div className="card-line rounded-xl p-5">
        <p className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-muted">Create Upsell (published immediately)</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <input className="field" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="xe-upsell-name" />
          <select className="field" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="xe-upsell-category">
            {data.categories.map((c) => <option key={c} value={c} className="bg-white">{c.replace("_", " ")}</option>)}
          </select>
          <input className="field" type="number" placeholder="Final price USD" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="xe-upsell-price" />
          <input className="field sm:col-span-2" placeholder="Customer-facing description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="xe-upsell-desc" />
          <select className="field" value={form.provider_type} onChange={(e) => setForm({ ...form, provider_type: e.target.value })} data-testid="xe-upsell-provider">
            <option value="xe" className="bg-white">XE provides (100% XE)</option>
            <option value="partner" className="bg-white">Partner provides (100% partner)</option>
            <option value="vendor" className="bg-white">Third-party vendor (set terms)</option>
          </select>
        </div>
        <div className="mt-3">
          <p className="field-label">Locations (customers only see the upsell at selected locations)</p>
          <div className="flex flex-wrap gap-2" data-testid="xe-upsell-locations">
            {(data.all_locations || []).map((l) => (
              <button key={l.location_id} type="button"
                onClick={() => setForm({ ...form, locations: form.locations.includes(l.location_id) ? form.locations.filter((x) => x !== l.location_id) : [...form.locations, l.location_id] })}
                data-testid={`xe-upsell-loc-${l.location_id}`}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${form.locations.includes(l.location_id) ? "border-aqua bg-aqua/10 text-aqua" : "border-black/15 text-muted hover:border-aqua/50"}`}>
                {l.name}
              </button>
            ))}
            {!(data.all_locations || []).length && <span className="text-xs text-muted">No locations yet — create one under Partners & Fleet.</span>}
          </div>
        </div>
        <button onClick={create} disabled={!form.name || !form.price || !form.locations.length} className="btn-primary mt-3 !py-2.5 !text-xs disabled:opacity-40" data-testid="xe-upsell-create"><Plus size={13} /> Create & Publish</button>
      </div>

      {proposals.length > 0 && (
        <div className="card-line rounded-xl border-orange-500/40 p-5">
          <p className="mb-3 font-display text-sm font-bold uppercase tracking-widest text-orange-700">Partner proposals awaiting approval ({proposals.length})</p>
          {proposals.map((u) => (
            <div key={u.upsell_id} className="mb-3 rounded-lg border border-black/[0.08] p-4" data-testid={`proposal-${u.upsell_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-bold text-offwhite">{u.name}</span>
                <span className="text-xs text-muted">{u.category.replace("_", " ")} · by {u.provider_name}</span>
                <span className="text-xs text-muted">recommended ${u.pricing?.base_price}</span>
              </div>
              <p className="mt-1 text-xs text-muted">{u.description}</p>
              {u.category === "drone_footage" && (
                <p className="mt-1.5 text-[11px] text-muted">
                  Drone docs: {data.drone_required_docs.map((d) => `${u.documents?.[d] ? "✓" : "✗"} ${d.replace(/_/g, " ")}`).join(" · ")}
                </p>
              )}
              <div className="mt-3 flex gap-2">
                <button onClick={() => act(() => api.post(`/admin/upsells/${u.upsell_id}/status`, { status: "active" }), "Approved & published")} className="btn-primary !px-4 !py-2 !text-xs" data-testid={`approve-upsell-${u.upsell_id}`}>Approve & Publish</button>
                <button onClick={() => act(() => api.post(`/admin/upsells/${u.upsell_id}/status`, { status: "archived" }), "Rejected")} className="btn-ghost !px-4 !py-2 !text-xs !text-red-600 !border-red-500/40" data-testid={`reject-upsell-${u.upsell_id}`}>Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-3">
        {data.upsells.filter((u) => u.status !== "pending_approval").map((u) => (
          <div key={u.upsell_id} className="card-line rounded-xl p-5" data-testid={`xe-upsell-${u.upsell_id}`}>
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="font-display text-base font-bold text-offwhite">{u.name}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">{u.category.replace("_", " ")}</span>
              <span className="text-xs text-muted">provider: {u.provider_name}</span>
              <span className="text-xs font-semibold text-offwhite">${u.pricing?.base_price}</span>
              <span className="text-xs text-muted">split {u.split?.provider_pct}/{u.split?.xe_pct}</span>
              <span className={`ml-auto rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${u.status === "active" ? "border-emerald-500/40 bg-emerald-400/10 text-emerald-700" : "border-black/15 text-muted"}`}>{u.status}</span>
            </div>
            <p className="mt-1 text-[11px] text-muted/70">locations: {u.location_names?.join(", ") || "none"} · tax {u.tax_pct}% · refunds: {u.refund_rule}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {[["active", "Activate"], ["paused", "Pause"], ["archived", "Archive"]].filter(([s]) => s !== u.status).map(([s, label]) => (
                <button key={s} onClick={() => act(() => api.post(`/admin/upsells/${u.upsell_id}/status`, { status: s }), `${label}d`)} className="btn-ghost !px-3 !py-1.5 !text-[11px]" data-testid={`status-${s}-${u.upsell_id}`}>{label}</button>
              ))}
              <span className="text-[11px] text-muted/60">{u.audit?.length || 0} audit events</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
